import { SECTIONS, SECTION_KEYS, datedItems, phrase } from './model'
import type { RegisterData, SectionKey } from './model'

export function Overview({
  data,
  onOpen,
  onGoSection,
}: {
  data: RegisterData
  onOpen: (section: SectionKey, id: string) => void
  onGoSection: (section: SectionKey) => void
}) {
  const all = datedItems(data)
  const pastDue = all.filter((x) => x.n < 0)
  const soon = all.filter((x) => x.n >= 0 && x.n <= 45)

  // no date recorded, among record types that carry a date
  const noDate = SECTION_KEYS.filter((k) => SECTIONS[k].dateField).reduce((sum, k) => {
    const f = SECTIONS[k].dateField!
    return sum + data[k].filter((r) => !r[f]).length
  }, 0)

  // company assets on a personal login
  const personal = SECTION_KEYS.filter((k) => SECTIONS[k].flag).reduce((sum, k) => {
    const flag = SECTIONS[k].flag!
    return sum + data[k].filter((r) => flag(r) && flag(r) !== 'No one assigned').length
  }, 0)

  const withPassword = (['hosting', 'accounts', 'subscriptions'] as const).reduce(
    (sum, k) => sum + data[k].filter((r) => r.has_password).length,
    0,
  )
  const noPassword = (['hosting', 'accounts', 'subscriptions'] as const).reduce(
    (sum, k) => sum + data[k].length,
    0,
  ) - withPassword

  // calendar columns: Past due, then the next 12 months
  const base = new Date(new Date().toISOString().slice(0, 10))
  const columns = [{ label: 'Past due', past: true, items: pastDue }]
  for (let i = 0; i < 12; i++) {
    const d = new Date(base.getFullYear(), base.getMonth() + i, 1)
    const label =
      d.toLocaleString('en', { month: 'short' }) +
      (d.getMonth() === 0 ? ` '${String(d.getFullYear()).slice(2)}` : '')
    columns.push({
      label,
      past: false,
      items: all.filter((x) => {
        const t = new Date(x.when)
        return x.n >= 0 && t.getMonth() === d.getMonth() && t.getFullYear() === d.getFullYear()
      }),
    })
  }

  return (
    <>
      <section className="rail">
        <h2>Renewal calendar</h2>
        <p className="k">
          Everything with a date, in the month it falls. Analytix renewals bunch up in summer —
          that is why so many slipped at once. Click a mark to open it.
        </p>
        <div className="months">
          {columns.map((c, i) => (
            <div className={`mo${c.past ? ' past' : ''}`} key={i}>
              <div className="stack">
                {c.items.slice(0, 12).map((x) => (
                  <button
                    key={x.section + x.id}
                    className={`bit ${
                      c.past ? 'r' : x.n <= 45 ? 'a' : x.section === 'subscriptions' ? 'b' : ''
                    }`}
                    title={`${x.name} — ${phrase(x.n)}`}
                    onClick={() => onOpen(x.section, x.id)}
                  />
                ))}
              </div>
              <b>
                {c.label}
                {c.items.length ? ` ${c.items.length}` : ''}
              </b>
            </div>
          ))}
        </div>
        <div className="legend">
          <span>
            <i style={{ background: 'var(--red)' }} />
            Past due
          </span>
          <span>
            <i style={{ background: 'var(--amber)' }} />
            Within 45 days
          </span>
          <span>
            <i style={{ background: 'var(--green)' }} />
            Domain
          </span>
          <span>
            <i style={{ background: 'var(--blue)' }} />
            Subscription
          </span>
        </div>
      </section>

      <div className="cols">
        <div className="card">
          <h3>Past due</h3>
          <p className="k">Check the registrar, then renew.</p>
          <div className={`big r`}>{pastDue.length}</div>
          <MiniList items={pastDue} empty="Nothing overdue." onOpen={onOpen} />
        </div>

        <div className="card">
          <h3>Next 45 days</h3>
          <p className="k">Confirm auto-renew and the card on file.</p>
          <div className={`big a`}>{soon.length}</div>
          <MiniList items={soon} empty="Nothing due soon." onOpen={onOpen} />
        </div>

        <div className="card">
          <h3>Status</h3>
          <p className="k">Things worth a look across the whole register.</p>
          <button type="button" className="mini" onClick={() => onGoSection('accounts')}>
            <span>Passwords on file</span>
            <span>{withPassword}</span>
          </button>
          <button type="button" className="mini" onClick={() => onGoSection('accounts')}>
            <span>Logins with no password on file</span>
            <span>{noPassword}</span>
          </button>
          <button type="button" className="mini" onClick={() => onGoSection('accounts')}>
            <span>Company assets on a personal login</span>
            <span>{personal}</span>
          </button>
          <button type="button" className="mini" onClick={() => onGoSection('domains')}>
            <span>Records with no date recorded</span>
            <span>{noDate}</span>
          </button>
          <p className="hint">
            A personal login means the asset can walk out the door when the person does.
          </p>
        </div>
      </div>
    </>
  )
}

function MiniList({
  items,
  empty,
  onOpen,
}: {
  items: ReturnType<typeof datedItems>
  empty: string
  onOpen: (section: SectionKey, id: string) => void
}) {
  if (!items.length) return <p className="k" style={{ margin: '8px 0 0' }}>{empty}</p>
  return (
    <>
      {items.slice(0, 7).map((x) => (
        <button
          type="button"
          className="mini"
          key={x.section + x.id}
          onClick={() => onOpen(x.section, x.id)}
        >
          <span>{x.name}</span>
          <span>{phrase(x.n)}</span>
        </button>
      ))}
    </>
  )
}
